import { v4 as uuidv4 } from 'uuid';

import { Tasks } from '@air/api';
import { type Account, type Board } from '@air/api/types';
import { buildClientImgixUrl } from '@air/component-imgix-image';
import {
  type AppNode,
  type CanvasNodeExportPayload,
  EDITS_IMGIX_HOSTNAMES,
  getVideoMetadata,
  getVideoThumbnailUrl,
} from '@air/feature-edit-canvas';
import { reportErrorToBugsnag } from '@air/utils-error';

import { GalleryItemType } from '~/components/Gallery/types';
import { getAccountKey } from '~/swr-hooks/account/useAccount';
import { registerCanvasImportPreview } from '~/utils/canvasImportPreviewCache';
import { registerPlaceholders } from '~/utils/importPlaceholderTracking';
import { type useAddAssetsToAllViews } from '~/utils/mutateUtils/GalleryAssets';
import { createPlaceholderClip } from '~/utils/placeholderClip';
import { type CreateTaskParams } from '~/utils/taskUtils/useCreateTask';
import { registerUploadDimensions } from '~/utils/uploadDimensionsCache';

/** Valid S3 key format expected by the backend: <uuidv4>.<extension> */
const VALID_S3_KEY_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\.[a-zA-Z0-9]+$/i;

/**
 * Extract imgix media details from a canvas node.
 * Returns the URL, S3 key, bucket, and mediaType — or null if the node has no importable media.
 */
const getImgixMediaDetails = (node: AppNode) => {
  if (node.type !== 'image' && node.type !== 'video') {
    return null;
  }

  const url = node.data.media?.original ?? node.data.media?.image;

  if (!url) {
    return null;
  }

  try {
    const parsed = new URL(url);

    if (!parsed.hostname.endsWith('.imgix.net')) {
      return null;
    }

    const key = parsed.pathname.slice(1).split('?')[0];

    if (!VALID_S3_KEY_PATTERN.test(key)) {
      return null;
    }

    const mediaType: CanvasNodeExportPayload['mediaType'] = node.type === 'video' ? 'video' : 'image';
    const thumbnailUrl = node.type === 'video' ? node.data.thumbnailUrl : undefined;

    return {
      bucket: parsed.hostname.split('.')[0],
      key,
      mediaType,
      thumbnailUrl,
      url,
    };
  } catch {
    return null;
  }
};

const DEFAULT_IMGIX_DIMENSION = 400;

/** Fetch image metadata from imgix by requesting JSON output metadata */
const getImgixImageMetadata = async (originalUrl: string): Promise<{ height: number; size: number; width: number }> => {
  try {
    const res = await fetch(buildClientImgixUrl(originalUrl, { fm: 'json' }));
    const data = await res.json();

    const contentLength = Number(data['Content-Length']);

    return {
      height: Number(data.PixelHeight) || DEFAULT_IMGIX_DIMENSION,
      size: Number.isNaN(contentLength) ? -1 : contentLength,
      width: Number(data.PixelWidth) || DEFAULT_IMGIX_DIMENSION,
    };
  } catch {
    return { height: DEFAULT_IMGIX_DIMENSION, size: -1, width: DEFAULT_IMGIX_DIMENSION };
  }
};

/** Fetch file size via HEAD request (used for video files where imgix JSON doesn't include Content-Length). */
const getFileSize = async (url: string): Promise<number> => {
  try {
    const res = await fetch(url, { method: 'HEAD' });
    const cl = res.headers.get('Content-Length');

    return cl ? Number(cl) : -1;
  } catch {
    return -1;
  }
};

export type ResolvedMediaItem = {
  bucket: string;
  descriptor: string;
  height: number;
  key: string;
  mediaType: CanvasNodeExportPayload['mediaType'];
  size: number;
  thumbnailUrl?: string;
  url: string;
  width: number;
};

const resolveMediaUrls = async (
  mediaDetails: {
    bucket: string;
    key: string;
    mediaType: CanvasNodeExportPayload['mediaType'];
    thumbnailUrl?: string;
    url: string;
  }[],
): Promise<ResolvedMediaItem[]> => {
  const now = new Date().toISOString();

  return Promise.all(
    mediaDetails.map(async ({ url, key, bucket, mediaType, thumbnailUrl }) => {
      let metadata: { height: number; size: number; width: number };

      if (mediaType === 'video') {
        const vm = await getVideoMetadata(url);
        const size = await getFileSize(url);
        metadata = { height: vm.height, size, width: vm.width };
      } else {
        metadata = await getImgixImageMetadata(url);
      }

      return {
        bucket,
        descriptor: JSON.stringify({
          ETag: uuidv4(),
          Key: key,
          LastModified: now,
          Size: metadata.size,
        }),
        height: metadata.height,
        key,
        mediaType,
        size: metadata.size,
        thumbnailUrl,
        url,
        width: metadata.width,
      };
    }),
  );
};

/**
 * Resolve imgix media details + metadata for a set of canvas nodes.
 *
 * Contract for callers: only `node.type`, `node.data.media.original` /
 * `node.data.media.image`, and (for video) `node.data.thumbnailUrl` are read.
 * `useSaveCanvasCompositeToWorkspace` relies on this — if you start reading
 * additional fields, audit that caller (it constructs a synthetic AppNode
 * with placeholder id/position).
 */
export const resolveNodesMedia = async (nodes: AppNode[]): Promise<ResolvedMediaItem[]> => {
  const mediaDetails = nodes.map(getImgixMediaDetails).filter((d): d is NonNullable<typeof d> => d !== null);

  if (!mediaDetails.length) {
    return [];
  }

  return resolveMediaUrls(mediaDetails);
};

/** Resolve imgix media details + metadata from export payloads (used by board drop target). */
export const resolveExportPayloadsMedia = async (payloads: CanvasNodeExportPayload[]): Promise<ResolvedMediaItem[]> => {
  const mediaDetails = payloads.flatMap((p) => {
    try {
      const parsed = new URL(p.media.original ?? p.media.image ?? '');

      if (!EDITS_IMGIX_HOSTNAMES.has(parsed.hostname)) {
        return [];
      }

      const key = parsed.pathname.slice(1).split('?')[0];

      if (!VALID_S3_KEY_PATTERN.test(key)) {
        return [];
      }

      return [
        {
          bucket: parsed.hostname.split('.')[0],
          key,
          mediaType: p.mediaType,
          thumbnailUrl: p.thumbnailUrl,
          url: p.media.original ?? p.media.image ?? '',
        },
      ];
    } catch {
      return [];
    }
  });

  if (!mediaDetails.length) {
    return [];
  }

  return resolveMediaUrls(mediaDetails);
};

export type ImportDestinationBoard = Pick<Board, 'id' | 'title' | 'thumbnails'>;

type ImportCanvasNodesToBoardParams = {
  addAssetsToAllViews: ReturnType<typeof useAddAssetsToAllViews>['addAssetsToAllViews'];
  board?: ImportDestinationBoard;
  boardId?: string;
  createTask: (params: CreateTaskParams) => Promise<{ data?: { localTaskId: string; remoteTaskId: string } }>;
  queryClient: {
    getQueryData: (queryKey: ReturnType<typeof getAccountKey>) => Account | null | undefined;
  };
  refreshImports: () => void;
  resolved: ResolvedMediaItem[];
  workspaceId: string;
  workspaceImage: string;
  workspaceName: string;
};

type ImportCanvasNodesToBoardResult = {
  imported: boolean;
  parentBoardId: string | undefined;
};

const getParentBoardId = ({ board, boardId }: Pick<ImportCanvasNodesToBoardParams, 'board' | 'boardId'>) =>
  board?.id || boardId || undefined;

/**
 * Core import pipeline: create S3 import tasks, inject placeholder clips, and refresh.
 * Used by both the "Save to Workspace" modal flow and direct drag-to-board drops.
 */
export const importCanvasNodesToBoard = async ({
  addAssetsToAllViews,
  board,
  boardId,
  createTask,
  queryClient,
  refreshImports,
  resolved,
  workspaceId,
  workspaceImage,
  workspaceName,
}: ImportCanvasNodesToBoardParams): Promise<ImportCanvasNodesToBoardResult> => {
  const now = new Date().toISOString();
  const parentBoardId = getParentBoardId({ board, boardId });
  const bucketGroups = Map.groupBy(resolved, (item) => item.bucket);

  const taskResults = await Promise.all(
    Array.from(bucketGroups, ([bucket, items]) => {
      const files = items.map(({ descriptor }) => ({ descriptor }));

      return createTask({
        createTask: () =>
          Tasks.createTask({
            task: {
              args: {
                files,
                folders: undefined,
                parentBoardId,
                s3Credentials: JSON.stringify({ bucket }),
                source: 's3',
              },
              type: 'Importer',
            },
            workspaceId,
          }),
        immediateStart: false,
        task: {
          createdAt: now,
          metadata: {
            board,
            files,
            folders: undefined,
            numRootFiles: files.length,
            numRootFolders: 0,
            parentBoardId,
            source: 's3',
          },
          type: 'Importer',
          updatedAt: now,
          workspaceId,
        },
      });
    }),
  );

  const succeeded = taskResults.filter((result) => result.data);

  if (succeeded.length === 0) {
    return { imported: false, parentBoardId };
  }

  const account = queryClient.getQueryData(getAccountKey());

  const placeholderClips = resolved.map((item) => {
    const previewUrl = item.mediaType === 'video' ? (item.thumbnailUrl ?? getVideoThumbnailUrl(item.url)) : item.url;
    // Backend strips the file extension when storing importedName, so we register without it
    const importedName = item.key.replace(/\.[^.]+$/, '');
    registerCanvasImportPreview(importedName, previewUrl);
    registerUploadDimensions(importedName, { width: item.width, height: item.height });
    return createPlaceholderClip({
      accountId: account?.id ?? '',
      fileSize: item.size,
      height: item.height,
      mediaType: item.mediaType,
      mediaUrl: item.url,
      ownerAvatar: account?.avatar ?? null,
      ownerInitials: account ? `${account.firstName[0]}${account.lastName[0]}` : '',
      ownerName: account ? `${account.firstName} ${account.lastName}` : '',
      previewUrl,
      s3Key: item.key,
      width: item.width,
      workspaceId,
      workspaceImage,
      workspaceName,
    });
  });

  for (const result of succeeded) {
    if (result.data) {
      registerPlaceholders(result.data.localTaskId, placeholderClips, parentBoardId);
    }
  }

  try {
    await addAssetsToAllViews({
      assets: placeholderClips,
      parentBoardId,
      type: GalleryItemType.asset,
    });
  } catch (error) {
    reportErrorToBugsnag({
      context: 'importCanvasNodesToBoard: Failed to add import placeholders to gallery cache',
      error,
    });
  }

  refreshImports();

  return { imported: succeeded.length === taskResults.length, parentBoardId };
};
